import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Typography,
  Paper,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CropSquareSharpIcon from "@mui/icons-material/CropSquareSharp";
import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory";
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import PentagonRoundedIcon from "@mui/icons-material/PentagonRounded";
import DrawIcon from "@mui/icons-material/Draw";
import TextIcon from "@mui/icons-material/TextFields";
import ImageIcon from "@mui/icons-material/Image";
import { useData } from "../hooks/useData";

// Custom PaperComponent to make the dialog draggable and apply zoom animation
function PaperComponent(props) {
  const paperRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  // Center the dialog when it opens
  useEffect(() => {
    if (props.open && paperRef.current) {
      const dialogWidth = paperRef.current.offsetWidth;
      const dialogHeight = paperRef.current.offsetHeight;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // Calculate the centered position
      const centeredX = (windowWidth - dialogWidth) / 2;
      const centeredY = (windowHeight - dialogHeight) / 2;

      setPosition({
        x: centeredX,
        y: centeredY,
      });
    }
  }, [props.open]);

  // Reset position when dialog closes to re-center on next open
  useEffect(() => {
    if (!props.open) {
      setPosition({ x: 0, y: 0 });
    }
  }, [props.open]);

  const handleMouseDown = (e) => {
    setDragging(true);
    setStartPos({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (dragging) {
      setPosition({
        x: e.clientX - startPos.x,
        y: e.clientY - startPos.y,
      });
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  return (
    <Paper
      {...props}
      ref={paperRef}
      style={{
        position: "absolute",
        top: position.y,
        left: position.x,
        animation: props.open
          ? "zoomIn 300ms ease-in-out forwards"
          : "zoomOut 300ms ease-in-out forwards",
        transformOrigin: "center center",
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    />
  );
}

const LayersDraggable = ({ canvas, open, onClose }) => {
  const { setSelectedObject } = useData();
  const [canvasLayers, setCanvasLayers] = useState([]);
  const [prevCanvasLayers, setPrevCanvasLayers] = useState([]);
  const [isDirty, setIsDirty] = useState(false);

  // Initialize canvas layers when the dialog opens
  useEffect(() => {
    if (open && canvas) {
      const objects = canvas.getObjects().slice().reverse(); // Reverse to match UI order
      setPrevCanvasLayers(objects.map((obj) => obj.toJSON())); // Store JSON for reverting
      setCanvasLayers(objects);
      setIsDirty(false);
    }
  }, [open, canvas]);

  const handleSaveChanges = () => {
    setIsDirty(false);
    onClose();
  };

  const handleClose = () => {
    if (isDirty && canvas) {
      // Revert changes by clearing canvas and restoring previous state
      canvas.remove(...canvas.getObjects());
      Promise.all(prevCanvasLayers.map((obj) => canvas._objectsToEnliven(obj)))
        .then((enlivenedObjects) => {
          enlivenedObjects.forEach((obj) => canvas.add(obj));
          canvas.requestRenderAll();
        })
        .catch((err) => console.error("Error reverting layers:", err));
    }
    setIsDirty(false);
    onClose();
  };

  const handleLayerSelect = (index) => {
    const selectedLayer = canvasLayers[index];
    const objects = canvas.getObjects();
    objects.forEach((layer) => {
      layer.isSelected = false;
    });

    selectedLayer.isSelected = true;
    selectedLayer.visible = true;
    canvas.setActiveObject(selectedLayer);
    setSelectedObject(selectedLayer);

    const updatedLayers = objects.slice().reverse();
    setCanvasLayers(updatedLayers);
    canvas.requestRenderAll();
    setIsDirty(true);
  };

  const handleRemoveLayer = (index) => {
    if (canvas) {
      const layer = canvasLayers[index];
      canvas.remove(layer);
      setSelectedObject(null);
      const updatedLayers = canvas.getObjects().slice().reverse();
      setCanvasLayers(updatedLayers);
      canvas.requestRenderAll();
      setIsDirty(true);
    }
  };

  const handleGroupExpand = (index) => {
    if (canvas) {
      const layer = canvasLayers[index];
      if (layer.type === "group") {
        layer._objects.forEach((obj) => {
          const clonedObj = obj.clone();
          canvas.add(clonedObj);
        });
        canvas.remove(layer);
        setSelectedObject(null);
        const updatedLayers = canvas.getObjects().slice().reverse();
        setCanvasLayers(updatedLayers);
        canvas.requestRenderAll();
        setIsDirty(true);
      }
    }
  };

  const handleSetVisibility = (index) => {
    const layer = canvasLayers[index];
    layer.visible = !layer.visible;
    if (!layer.visible) {
      layer.isSelected = false;
      canvas.discardActiveObject();
    }
    setSelectedObject(layer.visible ? layer : null);
    const updatedLayers = canvas.getObjects().slice().reverse();
    setCanvasLayers(updatedLayers);
    canvas.requestRenderAll();
    setIsDirty(true);
  };

  const handleSendDownStack = (index) => {
    const layer = canvasLayers[index];
    canvas.sendObjectBackwards(layer, true);
    canvas.discardActiveObject();
    setSelectedObject(layer);
    const updatedLayers = canvas.getObjects().slice().reverse();
    setCanvasLayers(updatedLayers);
    canvas.requestRenderAll();
    setIsDirty(true);
  };

  const handleSendUpStack = (index) => {
    const layer = canvasLayers[index];
    canvas.bringObjectForward(layer, true);
    canvas.discardActiveObject();
    setSelectedObject(layer);
    const updatedLayers = canvas.getObjects().slice().reverse();
    setCanvasLayers(updatedLayers);
    canvas.requestRenderAll();
    setIsDirty(true);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperComponent={(props) => <PaperComponent {...props} open={open} />}
      aria-labelledby="draggable-dialog-title"
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.2)", // Slight transparency
        },
      }}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "#fff",
          boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
        },
        "@keyframes zoomIn": {
          "0%": {
            transform: "scale(0)",
            opacity: 0,
          },
          "100%": {
            transform: "scale(1)",
            opacity: 1,
          },
        },
        "@keyframes zoomOut": {
          "0%": {
            transform: "scale(1)",
            opacity: 1,
          },
          "100%": {
            transform: "scale(0)",
            opacity: 0,
          },
        },
      }}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{
          color: "#ffffff",
          backgroundColor: "#0d2445",
          fontSize: "20px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Six-dot drag handle (portrait orientation) */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginRight: "8px" }}
        >
          <circle cx="4" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="12" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="12" r="1.5" fill="#ffffff" />
        </svg>
        Layers Toolbar
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {canvasLayers && canvasLayers.length > 0 ? (
            <List dense disablePadding>
              {canvasLayers.map((canvasLayer, index) => (
                <ListItem
                  key={index}
                  divider={true}
                  sx={{ textAlign: "left" }}
                  secondaryAction={
                    <IconButton onClick={() => handleRemoveLayer(index)}>
                      <DeleteOutlinedIcon />
                    </IconButton>
                  }
                >
                  <IconButton onClick={() => handleLayerSelect(index)}>
                    {canvasLayer.isSelected ? (
                      <RadioButtonCheckedIcon
                        sx={{ fontSize: 14, paddingRight: 2 }}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        sx={{ fontSize: 14, paddingRight: 2 }}
                      />
                    )}
                  </IconButton>
                  <IconButton onClick={() => handleSetVisibility(index)}>
                    {canvasLayer.visible ? (
                      <VisibilityIcon />
                    ) : (
                      <VisibilityOffIcon />
                    )}
                  </IconButton>
                  <IconButton onClick={() => handleSendUpStack(index)}>
                    <ArrowUpwardIcon />
                  </IconButton>
                  <IconButton onClick={() => handleSendDownStack(index)}>
                    <ArrowDownwardIcon />
                  </IconButton>
                  <IconButton onClick={() => handleGroupExpand(index)}>
                    {canvasLayer.type === "group" ? (
                      <ArrowDropDownIcon />
                    ) : (
                      <ArrowRightIcon />
                    )}
                  </IconButton>
                  <ListItemButton>
                    <ListItemText>
                      {canvasLayer.type === "circle" && <CircleOutlinedIcon />}
                      {canvasLayer.type === "rect" && <CropSquareSharpIcon />}
                      {canvasLayer.type === "triangle" && <ChangeHistoryIcon />}
                      {canvasLayer.type === "ellipse" && (
                        <PanoramaFishEyeIcon />
                      )}
                      {canvasLayer.type === "line" && <HorizontalRuleIcon />}
                      {canvasLayer.type === "path" && <DrawIcon />}
                      {canvasLayer.type === "polygon" && (
                        <PentagonRoundedIcon />
                      )}
                      {canvasLayer.type === "i-text" && <TextIcon />}
                      {canvasLayer.text && canvasLayer.text.slice(0, 9)}
                      {canvasLayer.type === "image" && <ImageIcon />}
                      {canvasLayer.type === "group" && "Group"}
                    </ListItemText>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography sx={{ marginTop: 2 }}>No layers available</Typography>
          )}
          <DialogActions>
            <Button autoFocus onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSaveChanges}>Save Changes</Button>
          </DialogActions>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

export default LayersDraggable;
